import {
  Box,
  Drawer,
  List,
  CircularProgress,
  Alert,
  IconButton,
  Toolbar,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material';
import { Link } from '@tanstack/react-router';
import { useMenu } from '../../features/menu';
import { MenuItem } from './MenuItem';
import { useAppSelector } from '../../app/store';

type SidebarProps = {
  width?: number;
  open?: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
};

export const Sidebar = ({
  width = 280,
  open = true,
  onClose,
  variant = 'persistent',
}: SidebarProps) => {
  const { menuItems, loading, error, loadMenu, clearError } = useMenu();
  const logoPageUrl = useAppSelector((state) => state.auth.logoPageUrl);
  const logoPagePath =
    logoPageUrl?.startsWith('/') && !logoPageUrl.startsWith('//') ? logoPageUrl : '/';

  const handleRetry = () => {
    clearError();
    loadMenu();
  };

  // Close sidebar on navigation only for temporary (mobile) drawers
  const menuItemOnClose = variant === 'temporary' ? onClose : undefined;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar />

      <Divider />

      <List sx={{ py: 0 }}>
        <ListItemButton
          component={Link}
          to={logoPagePath}
          onClick={menuItemOnClose}
          sx={{
            minHeight: 48,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <HomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Home"
            primaryTypographyProps={{ fontSize: '1rem', fontWeight: 400 }}
          />
        </ListItemButton>
      </List>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            action={
              <IconButton color="inherit" size="small" onClick={handleRetry}>
                <RefreshIcon />
              </IconButton>
            }
            sx={{ m: 2 }}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && menuItems.length === 0 && (
          <Alert severity="info" sx={{ m: 2 }}>
            No menu items available
          </Alert>
        )}

        {!loading && !error && menuItems.length > 0 && (
          <List sx={{ py: 1 }}>
            {menuItems.map((item) => (
              <MenuItem key={item.id} item={item} onClose={menuItemOnClose} />
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        width: open ? width : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          overflow: 'hidden',
        },
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
